import type { CatalogueItem } from '@/models/Catalogue';
import {
  CatalogueItemCategory,
  PromotionRequestStatus,
} from '@/models/Catalogue';
import type { CatalogueProjectBom } from '@/models/Projects';
import { Permission } from '@/models/Roles';
import useAuthStore from '@/store/AuthStore';

/**
 * Role-based category visibility mapping.
 * Defines which item categories each role can view in the catalogue.
 */
const ROLE_VISIBLE_CATEGORIES: Record<string, CatalogueItemCategory[]> = {
  solutions: [CatalogueItemCategory.CUSTOM, CatalogueItemCategory.STANDARD],
  concept_engineering: [
    CatalogueItemCategory.DUMMY,
    CatalogueItemCategory.STANDARD,
  ],
  sales: [CatalogueItemCategory.STANDARD],
  solutions_poweruser: [
    CatalogueItemCategory.DUMMY,
    CatalogueItemCategory.CUSTOM,
    CatalogueItemCategory.STANDARD,
  ],
  admin: [
    CatalogueItemCategory.DUMMY,
    CatalogueItemCategory.CUSTOM,
    CatalogueItemCategory.STANDARD,
  ],
};

/**
 * Composable for catalogue item permission checks.
 * Call this once in setup() to get permission check functions.
 */
export function useCataloguePermissions() {
  const authStore = useAuthStore();

  /**
   * Get the list of categories visible to the current user based on their role.
   */
  function getVisibleCategories(): CatalogueItemCategory[] {
    const { role } = authStore;
    if (!role) {
      return [CatalogueItemCategory.STANDARD];
    }
    return (
      ROLE_VISIBLE_CATEGORIES[role] ?? [
        CatalogueItemCategory.CUSTOM,
        CatalogueItemCategory.STANDARD,
        CatalogueItemCategory.DUMMY,
      ]
    );
  }

  /**
   * Determines if the current user can edit a catalogue item.
   * Uses the canEdit field provided by the API.
   */
  function canEditItem(item: CatalogueItem): boolean {
    return item.canEdit ?? false;
  }

  /**
   * Determines if the current user can edit a BOM.
   * Uses the canEdit field provided by the API.
   */
  function canEditBom(bom: CatalogueProjectBom): boolean {
    return bom.canEdit ?? false;
  }

  /**
   * Check if user has basic catalogue update permission (for duplicate action).
   */
  function canDuplicateItem(): boolean {
    return authStore.hasPermission(Permission.UPDATE_CATALOGUE);
  }

  /**
   * Check if user can request promotion for an item.
   * User must: have UPDATE_CATALOGUE, NOT have UPDATE_CATALOGUE_ALL, own the item,
   * item must be custom category, and no pending request exists.
   */
  function canRequestPromotion(item: CatalogueItem): boolean {
    const hasUpdatePermission = authStore.hasPermission(
      Permission.UPDATE_CATALOGUE,
    );
    const hasPowerPermission = authStore.hasPermission(
      Permission.UPDATE_CATALOGUE_ALL,
    );
    const isOwner = item.creatorUuid === authStore.user.uuid;
    const isCustom = item.category === CatalogueItemCategory.CUSTOM;
    const hasPendingRequest =
      item.promotionRequest?.status === PromotionRequestStatus.PENDING;

    return (
      hasUpdatePermission &&
      !hasPowerPermission &&
      isOwner &&
      isCustom &&
      !hasPendingRequest
    );
  }

  /**
   * Check if user can create a revision of a standard item.
   * Requires UPDATE_CATALOGUE_ALL, item must be standard, and must not already have a next version.
   */
  function canCreateRevision(item: CatalogueItem): boolean {
    const hasPowerPermission = authStore.hasPermission(
      Permission.UPDATE_CATALOGUE_ALL,
    );
    const isStandard = item.category === CatalogueItemCategory.STANDARD;
    const hasNoNextVersion = !item.nextVersionUuid;
    return hasPowerPermission && isStandard && hasNoNextVersion;
  }

  /**
   * Check if user can cancel a promotion request (must own item and request is pending).
   */
  function canCancelPromotion(item: CatalogueItem): boolean {
    const isOwner = item.creatorUuid === authStore.user.uuid;
    return (
      isOwner &&
      item.promotionRequest?.status === PromotionRequestStatus.PENDING
    );
  }

  return {
    getVisibleCategories,
    canEditItem,
    canEditBom,
    canDuplicateItem,
    canCreateRevision,
    canRequestPromotion,
    canCancelPromotion,
  };
}
